import React, { useState } from 'react';

import { faEye, faSync, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ButtonVariants } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { useOpen } from 'hooks/useOpen';
import { BackButton, Button } from 'shared-components';

import CreateTemplateModal from './CreateTemplateModal';

type Props = {
  onReset: () => void;
  onDiscard: () => void;
  isPreviewEnabled: boolean;
  handlePreviewClick: () => void;
  filtersBlockRef: React.RefObject<any>;
  isEditMode?: boolean;
  templateId?: string;
};

const CreateReportTemplateHeader: React.FC<Props> = ({
  onReset,
  onDiscard,
  isPreviewEnabled,
  handlePreviewClick,
  filtersBlockRef,
  isEditMode,
  templateId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpenTemplateModal, handleOnOpenTemplateModal, handleOnCloseTemplateModal] = useOpen();
  const [templateName, setTemplateName] = useState('');

  const handleCreateClick = () => {
    const name = filtersBlockRef.current?.getTemplateName?.() || '';
    setTemplateName(name);
    handleOnOpenTemplateModal();
  };

  return (
    <div data-cy="report-templates-create-page" className="flex flex-col">
      <header className="flex items-center justify-between py-3 px-5 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center">
          <BackButton label="" onClick={() => navigate(ROUTES.reportTemplates)} className="m-0 p-0" />
          <h1 className="text-xl ml-2">{t('EasyQueryBuilder')}</h1>
        </div>
        <div className="flex">
          <Button className="ml-3" variant={ButtonVariants.OUTLINE} leftIcon={faSync} onClick={onReset}>
            {t('Reset')}
          </Button>
          <Button
            className="ml-3"
            variant={isPreviewEnabled ? ButtonVariants.OUTLINE : ButtonVariants.DISABLED}
            leftIcon={faEye}
            onClick={handlePreviewClick}
            disabled={!isPreviewEnabled}
          >
            {t('Preview')}
          </Button>
          <Button className="ml-3" variant={ButtonVariants.DESTRUCTIVE} leftIcon={faTrash} onClick={onDiscard}>
            {t('Discard')}
          </Button>
          <Button
            className="ml-3"
            variant={isPreviewEnabled ? ButtonVariants.PRIMARY : ButtonVariants.DISABLED}
            disabled={!isPreviewEnabled}
            onClick={handleCreateClick}
          >
            {t('Save')}
          </Button>
          {isOpenTemplateModal && (
            <CreateTemplateModal
              isOpenModal={isOpenTemplateModal}
              onCloseModal={handleOnCloseTemplateModal}
              getConfiguration={() => {
                const config = filtersBlockRef.current?.getCurrentConfiguration?.();
                return config;
              }}
              selectedColumnsData={filtersBlockRef.current?.getSelectedColumnsData() ?? []}
              initialName={templateName}
              filtersBlockRef={filtersBlockRef}
              isEditMode={isEditMode}
              templateId={templateId}
            />
          )}
        </div>
      </header>
    </div>
  );
};

export default CreateReportTemplateHeader;
